import { CreateTeamInput, Team, UpdateTeamInput } from "@/types/team";

import { request } from "@/helper/http";
import { invalidatesOn } from "@/helper/invalidatesOn";
import { APIQuery, APIResponse } from "@/types/global";
import { API_PATH } from "@constants/config";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

export const createTeam = (data: CreateTeamInput) => {
  return request({
    url: API_PATH.TEAMS,
    method: "post",
    data,
  });
};

export function deleteTeam(id: string) {
  return request({
    url: `${API_PATH.TEAMS}${id}/`,
    method: "delete",
  });
}

export function bulkDeleteTeam(ids: string) {
  return request({
    url: `${API_PATH.BULK_DELETE_TEAM}`,
    method: "delete",
    params: { ids },
  });
}

export const updateTeam = (id: string, data: UpdateTeamInput) => {
  return request({
    url: `${API_PATH.TEAMS}${id}/`,
    method: "patch",
    data,
  });
};

export const getTeams = async (params: APIQuery) => {
  const response: APIResponse<Team> = await request({ url: API_PATH.TEAMS, method: "get", params });
  const normalizedTeams = response.results.map((item, i) => ({ ...item, number: i + 1 + (params?.offset ?? 0) }));
  return { ...response, results: normalizedTeams };
};

export const getAllTeams = async (noNeedPaging?: boolean) => {
  let limit = 300;
  let teams: any[] = [];

  let response = await getTeams({ limit, offset: 0 });

  if (response) {
    teams = response.results;
    if (noNeedPaging) {
      limit = response.count - teams.length;
    }
    while (teams.length < response.count) {
      const offset = teams.length;
      const params: APIQuery = { limit, offset };
      response = await getTeams(params);
      teams.push(...response.results);
    }
  }

  return teams || [];
};

export const teamApi = createApi({
  reducerPath: "teamApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Teams"],
  endpoints: (build) => ({
    getTeams: build.query({
      queryFn: async (params: APIQuery) => {
        try {
          const data = await getTeams(params);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["Teams"],
    }),
    createTeam: build.mutation({
      queryFn: async (params: CreateTeamInput) => {
        try {
          const data = await createTeam(params);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Teams"], error: [] }),
    }),
    updateTeam: build.mutation({
      queryFn: async (params: { id: string; data: UpdateTeamInput }) => {
        try {
          const data = await updateTeam(params.id, params.data);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Teams"], error: [] }),
    }),
    deleteTeam: build.mutation({
      queryFn: async (id: string) => {
        try {
          const data = await deleteTeam(id);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Teams"], error: [] }),
    }),
    bulkDeleteTeam: build.mutation({
      queryFn: async (ids: string) => {
        try {
          const data = await bulkDeleteTeam(ids);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Teams"], error: [] }),
    }),
  }),
});

export const {
  useGetTeamsQuery,
  useLazyGetTeamsQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useBulkDeleteTeamMutation,
} = teamApi;
